import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, ChevronDown } from 'lucide-react';
import { useToast } from './Toast';

/**
 * Admin-only reusable export button.
 *
 * When a single option is given, renders a plain button.
 * When multiple options are given, renders a dropdown menu.
 *
 * Each option: { id, label, description?, disabled?, onExport: () => void | Promise<void> }
 */
export default function ExportButton({ options }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const wrapRef = useRef(null);

  const allDisabled = options.length > 0 && options.every((o) => o.disabled);

  // Close dropdown on outside click or Escape key
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = async (opt) => {
    setOpen(false);
    if (opt.disabled || loadingId != null) return;
    setLoadingId(opt.id);
    try {
      await opt.onExport();
      toast(opt.successMessage || t('export.csvExported'), 'success');
    } catch (err) {
      console.error('[Floora] CSV export failed:', err);
      toast(err?.message || t('export.exportFailed'), 'error');
    } finally {
      setLoadingId(null);
    }
  };

  const isLoading = loadingId != null;

  // ── Single-option: plain button ──────────────────────────────────
  if (options.length === 1) {
    const opt = options[0];
    return (
      <button
        className="btn btn-sm btn-secondary"
        onClick={() => run(opt)}
        disabled={isLoading || !!opt.disabled}
        title={opt.description}
        aria-label={opt.label || t('export.csv')}
      >
        <Download size={14} />
        {isLoading ? t('export.exporting') : (opt.label || t('export.csv'))}
      </button>
    );
  }

  // ── Multi-option: dropdown ───────────────────────────────────────
  return (
    <div className="export-btn" ref={wrapRef}>
      <button
        className="btn btn-sm btn-secondary export-btn__trigger"
        onClick={() => { if (!isLoading && !allDisabled) setOpen((v) => !v); }}
        disabled={isLoading || allDisabled}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('a11y.exportData')}
      >
        <Download size={14} />
        {isLoading ? t('export.exporting') : t('export.export')}
        <ChevronDown size={11} style={{ marginLeft: 1, opacity: 0.7 }} />
      </button>

      {open && (
        <div className="export-btn__menu" role="menu">
          {options.map((opt) => (
            <button
              key={opt.id}
              className="export-btn__item"
              role="menuitem"
              onClick={() => run(opt)}
              disabled={!!opt.disabled}
            >
              <span className="export-btn__item-label">{opt.label}</span>
              {opt.description && (
                <span className="export-btn__item-desc">{opt.description}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
